import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Formik } from 'formik';
import { Button, Container, Form, Grid } from 'semantic-ui-react';
import { FormattedMessage /*, injectIntl, intlShape*/ } from 'react-intl';
import { FormComponents, DataList, Loader } from '../components';
import { Formatters } from '../utils';
import { PRICING, NET_PRICE, discountFields } from '../hoc/pricingAggregator';
import { formatPositiveNumber } from './utils';
const AdditionalText = styled.p`
  font-size: 0.875rem;
  font-weight: 600;
`;

/**
 * @function PricingForm
 * work with `withPricing` wrapper
 */
const PricingForm = ({
  error,
  loading,
  selected,
  fields,
  setInitialState,
  handleOnChange
}) => {
  const initialValues = {};
  discountFields.forEach(field => {
    initialValues[field.name] = '';
  });

  const getTotal = values => {
    let total = 0;

    if (selected) {
      total = selected[PRICING];
    }

    // loop through all discount fields
    discountFields.forEach(field => {
      const discount = values && values[field.name];
      if (discount) {
        total = total * ((100 - Number(discount)) / 100);
      }
    });

    total = selected[NET_PRICE] ? total + selected[NET_PRICE] : total;

    return total;
  };

  const handleReset = resetForm => {
    const firstField = fields.filter(field => field.idx === 0)[0];
    const values = { [firstField.name]: '' };

    setInitialState(firstField);
    resetForm({ ...initialValues, ...values });
  };

  // eslint-disable-next-line react/prop-types
  const renderFields = ({ handleChange, setValues, values }) => {
    const changeValue = obj => {
      const updated = { ...values, ...obj };

      // clear discount fields
      if (selected) {
        discountFields.forEach(field => {
          updated[field.name] = '';
        });
      }

      setValues(updated);
      return updated;
    };

    // 5 comes from design
    const limit = 5;
    let temp = [];
    let count = 0;
    for (let i = 0; i < fields.length; i += limit) {
      temp[count] = fields.slice(i, i + limit);
      count++;
    }

    return fields && fields.length ? (
      temp.map((chunk, i) => (
        <Form.Group key={i} className="fields--fluid">
          {chunk.map((field, idx) => (
            <Fragment key={`${i}-${idx}`}>
              <FormComponents.Select
                name={field.name}
                placeholder={`Select ${field.label}`}
                placeholderDisabled={false}
                label={field.label}
                options={field.options}
                onChange={e => {
                  const {
                    target: { value }
                  } = e;

                  // call formik handleChange to update values
                  handleChange(e);
                  // call wrapper handleOnChange to update snapshot
                  handleOnChange({
                    ...field,
                    idx: idx + i * limit,
                    value,
                    changeValue
                  });
                }}
                className="field--fixed"
              />
              {idx + i * limit === fields.length - 1 && loading && (
                <Loader active size="medium" />
              )}
            </Fragment>
          ))}
        </Form.Group>
      ))
    ) : (
      <Container textAlign="center">
        <Loader active inline size="medium" />
      </Container>
    );
  };

  const handleDiscountChange = (e, handleChange, values, setValues, field) => {
    const {
      target: { value }
    } = e;

    const newValue = formatPositiveNumber(value);
    handleChange(e);

    setValues({
      ...values,
      [field.name]: newValue
    });
  };

  return (
    <Formik initialValues={initialValues}>
      {({ handleChange, values, setValues, resetForm }) => (
        <Form error={!!error}>
          <div className="bg--secondary">
            {renderFields({ handleChange, setValues, values })}
          </div>

          {error && <FormComponents.FormError error={error} />}

          {selected && (
            <Fragment>
              <Grid>
                <Grid.Column textAlign="right">
                  {selected[PRICING] && (
                    <DataList
                      list={[
                        {
                          qa: 'srp-value',
                          title: 'SRP',
                          data: `${Formatters.toCurrency(
                            Number(selected[PRICING]).toFixed(2)
                          )}`
                        }
                      ]}
                      theme={['bold', 'pricing', 'tight']}
                    />
                  )}
                </Grid.Column>
              </Grid>

              <Grid>
                <Grid.Column computer={5} tablet={0} mobile={4} />
                <Grid.Column computer={11} tablet={16} mobile={12} as="footer">
                  <Form.Group inline className="bg--secondary">
                    {discountFields &&
                      discountFields.map((field, idx) => (
                        <FormComponents.Field
                          key={idx}
                          type="text"
                          name={field.name}
                          label={field.label}
                          onChange={e =>
                            handleDiscountChange(
                              e,
                              handleChange,
                              values,
                              setValues,
                              field
                            )
                          }
                          className="input--percentage"
                        />
                      ))}
                  </Form.Group>
                </Grid.Column>
              </Grid>

              <Grid>
                <Grid.Column textAlign="right">
                  {(selected[NET_PRICE] || selected[NET_PRICE] === 0) && (
                    <DataList
                      list={[
                        {
                          qa: 'net-price-value',
                          title: '+ Net Price',
                          data: `${Formatters.toCurrency(
                            Number(selected[NET_PRICE]).toFixed(2)
                          )}`
                        }
                      ]}
                      theme={['bold', 'big-font', 'tight', 'pricing']}
                    />
                  )}
                </Grid.Column>
              </Grid>

              <Grid>
                <Grid.Column textAlign="right">
                  {selected[PRICING] && (
                    <Fragment>
                      <DataList
                        list={[
                          {
                            qa: 'price-value',
                            title: 'Total',
                            data: (
                              <Fragment>
                                {`${Formatters.toCurrency(
                                  Number(getTotal(values)).toFixed(2)
                                )}`}
                                {/* TODO: CAD needs to be dealer location */}
                              </Fragment>
                            )
                          }
                        ]}
                        theme={['bolder', 'tight', 'pricing']}
                      />
                      <AdditionalText>
                        (
                        <FormattedMessage
                          id="description.pricing_note"
                          description="Description of what's included in price"
                          defaultMessage="{currency} - Includes discounts plus Net Price"
                          values={{ currency: selected.currency }}
                        />
                        )
                      </AdditionalText>
                    </Fragment>
                  )}
                </Grid.Column>
              </Grid>

              <Grid>
                <Grid.Column as="footer">
                  <Form.Field
                    type="reset"
                    control={Button}
                    size="small"
                    className={'field--right'}
                    data-qa="start-over-button"
                    onClick={() => handleReset(resetForm)}
                    primary
                  >
                    <FormattedMessage
                      id="cta.start_over"
                      description="Start Over button label"
                      defaultMessage="Start Over"
                    />
                  </Form.Field>

                  <small>
                    <strong>
                      <FormattedMessage
                        id="description.pricing_date"
                        description="Description of date"
                        defaultMessage="Pricing as of {date}"
                        values={{
                          date: Formatters.toUTCDay(selected.updated_at)
                        }}
                      />
                    </strong>
                  </small>
                </Grid.Column>
              </Grid>
            </Fragment>
          )}
        </Form>
      )}
    </Formik>
  );
};

PricingForm.propTypes = {
  // from withPricing wrapper
  fields: PropTypes.arrayOf(PropTypes.object),
  selected: PropTypes.object,
  setInitialState: PropTypes.func,
  handleOnChange: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  loading: PropTypes.bool
};
export default PricingForm;



// WEBPACK FOOTER //
// ./src/forms/PricingForm.js